import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from './button.js';

export interface DeleteConfirmModalProps {
  isOpen: boolean;
  title?: string;
  itemType?: string; // e.g. "Task", "Client", "Transaction", "Lead", "Time Entry", "Standup Entry", "SEO Item", "Project"
  itemName?: string;
  description?: string;
  confirmLabel?: string;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
}

export function DeleteConfirmModal({
  isOpen,
  title,
  itemType = 'Item',
  itemName,
  description,
  confirmLabel,
  onConfirm,
  onClose,
}: DeleteConfirmModalProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsDeleting(false);
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleConfirmClick = async () => {
    try {
      setIsDeleting(true);
      await onConfirm();
    } catch (err: any) {
      console.error('Delete action failed:', err);
      alert(err.message || 'Failed to delete');
    } finally {
      setIsDeleting(false);
    }
  };

  const modalTitle = title || `Delete ${itemType}?`;
  const defaultDescription = itemName
    ? `Are you sure you want to permanently delete "${itemName}"? This action cannot be undone.`
    : `Are you sure you want to permanently delete this ${itemType.toLowerCase()}? This action cannot be undone.`;

  return (
    <div
      className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-[#12211F] rounded-xl shadow-2xl max-w-sm w-full p-6 space-y-4 border border-slate-200 dark:border-brand-border animate-in zoom-in-95"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-dialog-title"
      >
        <div className="flex items-center space-x-3 text-red-600">
          <div className="p-2.5 bg-red-100 dark:bg-red-950/40 rounded-full shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <h3 id="delete-dialog-title" className="font-bold text-base text-slate-900 dark:text-brand-ink">
            {modalTitle}
          </h3>
        </div>

        <p className="text-xs text-slate-600 dark:text-brand-muted leading-relaxed">
          {description || defaultDescription}
        </p>

        <div className="flex justify-end space-x-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={isDeleting}
            className="text-xs"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={handleConfirmClick}
            disabled={isDeleting}
            className="bg-red-600 hover:bg-red-700 text-white text-xs flex items-center space-x-1"
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                <span>Deleting...</span>
              </>
            ) : (
              <span>{confirmLabel || `Yes, Delete ${itemType}`}</span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
